<template>
  <el-dialog
    :model-value="visible"
    title="Bobinas"
    width="800px"
    class="bobinas-modal"
    @update:model-value="handleCancelar"
    @close="handleCancelar"
    @opened="focusFila(0)"
  >
    <el-table
      :data="filas"
      border
      style="width: 100%"
      :max-height="320"
    >
      <el-table-column
        label="Ancho (mm)"
        min-width="140"
      >
        <template #default="{ row, $index }">
          <el-input-number
            :ref="(el) => setAnchoRef(el, $index)"
            v-model="row.Ancho"
            :min="0"
            :precision="2"
            :step="0.01"
            :controls="false"
            :formatter="formatter"
            :parser="parser"
            style="width: 100%"
            @focus="handleFocus"
            @blur="handleBlur(row.Ancho)"
          />
        </template>
      </el-table-column>

      <el-table-column
        label="Tipo de tratado"
        min-width="160"
      >
        <template #default="{ row }">
          <el-select
            v-model="row.CodigoTipoTratado"
            placeholder="Seleccionar"
            style="width: 100%"
          >
            <el-option
              v-for="opt in tipoTratadoOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column
        label=""
        width="90"
        fixed="right"
      >
        <template #default="{ row, $index }">
          <div style="display: flex; gap: 8px">
            <el-button
              size="small"
              :icon="IconContentCopy"
              style="margin-left: 0"
              @click="copiarFila(row)"
            />
            <el-button
              type="danger"
              size="small"
              :icon="IconDelete"
              style="margin-left: 0"
              @click="eliminarFila($index)"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div
      style="margin-top: 12px; display: flex; justify-content: space-between; align-items: center"
    >
      <el-button
        type="primary"
        plain
        :disabled="!permiteVariasBobinas"
        :title="
          permiteVariasBobinas
            ? ''
            : 'Sólo se puede cargar más de una bobina cuando la extrusión es el único proceso del elemento'
        "
        @click="agregarFila"
        >+ Bobina</el-button
      >
      <div style="display: flex; align-items: center; gap: 8px">
        <span style="font-size: 13px; color: #606266">Aprovechar ancho máximo</span>
        <el-switch
          v-model="aprovecharAnchoMaximoLocal"
          :disabled="filas.length !== 1"
        />
      </div>
    </div>

    <ExtrusionBobinasResumen
      style="margin-top: 16px"
      :bobinas="filas"
      :factor-multiplicacion="factorMultiplicacion"
      :recurso="recurso"
      :aprovechar-ancho-maximo="aprovecharAnchoMaximoLocal"
    />

    <el-alert
      v-if="errorAncho"
      :title="errorAncho"
      type="error"
      show-icon
      :closable="false"
      style="margin-top: 12px"
    />

    <template #footer>
      <el-button @click="handleCancelar">Cancelar</el-button>
      <el-button
        type="primary"
        @click="handleGuardar"
        >Guardar</el-button
      >
    </template>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import IconDelete from '~icons/mdi/delete';
import IconContentCopy from '~icons/mdi/content-copy';
import ExtrusionBobinasResumen from './ExtrusionBobinasResumen.vue';
import { MaterialService } from '@/services/material_service';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import api from '@/services/api';
import { useLocaleNumberInput } from '@/shared/composables/useLocaleNumberInput.js';

const {
  inputRef: localeInputRef,
  formatter,
  parser,
  handleFocus,
  handleBlur,
} = useLocaleNumberInput(2);

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  visible: { type: Boolean, default: false },
  factorMultiplicacion: { type: Number, default: 1 },
  // anchoMaximoMaquina:      { type: Number,  default: 0 },
  aprovecharAnchoMaximo: { type: Boolean, default: false },
  recurso: { type: Object, default: () => ({}) },
  // COTI-642: sólo se permiten anchos de bobina distintos cuando la extrusión es el único
  // proceso del elemento. Con otros procesos, el ancho lo gobierna la primera (y única) bobina.
  permiteVariasBobinas: { type: Boolean, default: true },
});

const emit = defineEmits(['save', 'update:visible']);

const tipoTratadoOptions = ref([]);

const filas = ref([]);
const anchoRefs = ref([]);
const aprovecharAnchoMaximoLocal = ref(false);

const setAnchoRef = (el, index) => {
  if (el) {
    anchoRefs.value[index] = el;
  }
};

const errorAncho = ref('');

const cargarTipoTratadoOptions = async () => {
  try {
    const result = await api.get('/tipo-tratado', { params: { Activo: true, page: 0 } });
    tipoTratadoOptions.value = (result?.data || []).map((tt) => ({
      value: tt.Codigo,
      label: tt.Descripcion,
    }));
  } catch (error) {
    tipoTratadoOptions.value = [];
  }
};

onBeforeMount(() => {
  cargarTipoTratadoOptions();
});

watch(
  () => props.visible,
  (abierto) => {
    if (abierto) {
      anchoRefs.value = [];
      filas.value = (props.modelValue || []).map((f) => ({ ...f }));
      if (filas.value.length === 0) {
        filas.value.push(nuevaFila());
      }
      aprovecharAnchoMaximoLocal.value = props.aprovecharAnchoMaximo ?? false;
      errorAncho.value = '';
    }
  },
);

watch(
  filas,
  (nuevasFilas) => {
    if (nuevasFilas.length !== 1) {
      aprovecharAnchoMaximoLocal.value = false;
    }
    errorAncho.value = '';
  },
  { deep: true },
);

const sumAnchos = computed(() => filas.value.reduce((sum, f) => sum + (Number(f.Ancho) || 0), 0));

const nuevaFila = () => ({
  Ancho: null,
  CodigoTipoTratado: null,
});

const focusFila = async (index) => {
  await nextTick();
  anchoRefs.value[index]?.focus();
};

const agregarFila = async () => {
  // COTI-642: con la extrusión compartiendo flujo, una sola bobina.
  if (!props.permiteVariasBobinas && filas.value.length >= 1) return;
  filas.value.push(nuevaFila());
  await focusFila(filas.value.length - 1);
};

const eliminarFila = (index) => {
  filas.value.splice(index, 1);
  anchoRefs.value.splice(index, 1);
};

const copiarFila = (row) => {
  filas.value.push({ ...row });
};

const handleGuardar = () => {
  errorAncho.value = '';
  if (props.recurso?.AnchoMaximo > 0 && sumAnchos.value > props.recurso?.AnchoMaximo) {
    errorAncho.value = `La suma de anchos (${sumAnchos.value.toFixed(2)} mm) supera el ancho máximo de la máquina (${props.recurso?.AnchoMaximo.toFixed(2)} mm).`;
    return;
  }
  emit('save', {
    bobinas: filas.value.map((f) => ({ ...f })),
    aprovecharAnchoMaximo: aprovecharAnchoMaximoLocal.value,
  });
  emit('update:visible', false);
};

const handleCancelar = () => {
  errorAncho.value = '';
  emit('update:visible', false);
};
</script>

<style>
.bobinas-modal.el-dialog {
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bobinas-modal .el-dialog__body {
  flex: 1;
  overflow-y: auto;
  max-height: calc(80vh - 120px);
}
</style>
